import Button, {
  ButtonVariantEnum,
} from "@booking/components/atoms/forms/Button";
import {
  closeDialog,
  openDialog,
  renderChildren,
} from "@booking/utils/helpers";
import { gaClickAttrs } from "@booking/utils/tracking";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import clsx from "clsx";
import React, { useEffect } from "react";

import { ColorEnum } from "@/enums";
import { type CommonProps } from "@/types";
import { createId } from "@/utils/helpers";

import styles from "./Dialog.module.css";

const ref = "dialog";

interface DialogProps extends Partial<CommonProps> {
  autoOpen?: boolean;
  children: React.ReactNode;
  fullscreen?: boolean;
  header?: React.ReactNode;
  lightbox?: boolean;
  modal?: boolean;
  onClose?: () => void;
  hideCloseButton?: boolean;
}

function closeButton(eventGroup?: string) {
  return (
    <Button
      className={styles.close}
      color={ColorEnum.WHITE}
      formMethod="dialog"
      icon={faXmark}
      rounded
      title="Close"
      variant={ButtonVariantEnum.SOLID}
      onClick={closeDialog}
      {...gaClickAttrs({ eventGroup })}
    >
      <span className="sr-only">Close</span>
    </Button>
  );
}

function useDialog(props: DialogProps) {
  const { eventGroup, fullscreen, lightbox, modal, onClose, hideCloseButton } =
    props;
  const className = clsx(
    styles[ref],
    fullscreen && styles.fullscreen,
    lightbox && !fullscreen && styles.lightbox,
    props.className,
  );
  const id = props.id || createId(ref);

  return {
    autoOpen: !!props.autoOpen,
    hideCloseButton,
    children: props.children,
    className,
    eventGroup,
    header: props.header,
    id,
    onClick: (e: any) => {
      if (onClose) {
        onClose();
      }

      if (!modal) {
        closeDialog(e);
      }
    },
  };
}

const Dialog = React.forwardRef<HTMLDialogElement, DialogProps>(
  function Dialog(props, forwardRef) {
    const {
      autoOpen,
      hideCloseButton,
      children,
      className,
      eventGroup,
      header,
      id,
      onClick,
    } = useDialog(props);

    useEffect(() => {
      if (autoOpen) {
        openDialog(id, eventGroup);
      }
    });

    if (!children) {
      return null;
    }

    return (
      <dialog className={className} id={id} ref={forwardRef} onClick={onClick}>
        <header>
          {header}
          {!hideCloseButton && closeButton(eventGroup)}
        </header>

        <article>{renderChildren(id, children)}</article>
      </dialog>
    );
  },
);

Dialog.displayName = "Dialog";

export default Dialog;
export { Dialog, type DialogProps };
